import { useQuery, gql, useMutation } from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLQueryFont_GetFontArgs,
  GQLFontAsset,
  GQLFontAssetConnection,
  GQLPartyType,
  GQLQueryFont_GetFontsByMerchantArgs,
  GQLQueryFont_GetFontsByPlatformArgs,
  GQLQueryFont_GetFontsByProducerArgs,
  GQLMutationFont_CreateFontArgs,
  GQLMutationFont_UpdateFontArgs,
  GQLMutationFont_ImportFontMediaArgs,
  GQLQueryFont_GetFontsArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { FONT_ASSET_FIELDS } from './fragments/font';
import { OperationsHelper } from './OperationHelper';

export class FontOperation {
  fontAssetArrayFromResponse(data: any): GQLFontAsset[] {
    if (data.Font_getFonts !== undefined) {
      return data.Font_getFonts;
    }

    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'fonts');
  }

  fontAssetFromResponse(data: any): GQLFontAsset {
    if (data.Font_getFont !== undefined) {
      return data.Font_getFont;
    }
    throw new Exception(`Cannot handle input data fontAssetToFontAsset`);
  }

  createFont() {
    const mutation = gql`
      ${FONT_ASSET_FIELDS}
      mutation createFont($input: FontAssetInput!) {
        Font_createFont(input: $input) {
          ...FontAssetFields
        }
      }
    `;
    return useMutation<GQLFontAsset, GQLMutationFont_CreateFontArgs>(mutation);
  }

  updateFont() {
    const mutation = gql`
      ${FONT_ASSET_FIELDS}
      mutation updateFont($fontId: ID!, $input: FontAssetInput!) {
        Font_updateFont(fontId: $fontId, input: $input) {
          ...FontAssetFields
        }
      }
    `;
    return useMutation<GQLFontAsset, GQLMutationFont_UpdateFontArgs>(mutation);
  }

  importFontMedia() {
    const mutation = gql`
      mutation importFontMedia($fontId: ID!, $file: Upload!) {
        Font_importFontMedia(fontId: $fontId, file: $file)
      }
    `;
    return useMutation<Boolean, GQLMutationFont_ImportFontMediaArgs>(mutation);
  }

  getFontsByPlatform(args: GQLQueryFont_GetFontsByPlatformArgs) {
    const query = gql`
      ${FONT_ASSET_FIELDS}
      query getFonts($pageSize: Int = 10) {
        Font_getFontsByPlatform(pageSize: $pageSize) {
          fonts {
            ...FontAssetFields
          }
        }
      }
    `;
    return useQuery<GQLFontAssetConnection, GQLQueryFont_GetFontsByPlatformArgs>(query, {
      variables: args
    });
  }

  getFontsByProducer(args: GQLQueryFont_GetFontsByProducerArgs) {
    const query = gql`
      ${FONT_ASSET_FIELDS}
      query getFonts($producerPartyId: ID!, $pageSize: Int = 10) {
        Font_getFontsByProducer(producerPartyId: $producerPartyId, pageSize: $pageSize) {
          fonts {
            ...FontAssetFields
          }
        }
      }
    `;
    return useQuery<GQLFontAssetConnection, GQLQueryFont_GetFontsByProducerArgs>(query, {
      variables: args
    });
  }

  getFontsByMerchant(args: GQLQueryFont_GetFontsByMerchantArgs) {
    const query = gql`
      ${FONT_ASSET_FIELDS}
      query getFonts($merchantPartyId: ID!, $pageSize: Int = 10) {
        Font_getFontsByMerchant(merchantPartyId: $merchantPartyId, pageSize: $pageSize) {
          fonts {
            ...FontAssetFields
          }
        }
      }
    `;
    return useQuery<GQLFontAssetConnection, GQLQueryFont_GetFontsByMerchantArgs>(query, {
      variables: args
    });
  }

  getFonts(args: GQLQueryFont_GetFontsArgs) {
    const query = gql`
      ${FONT_ASSET_FIELDS}
      query getFonts($fontIds: [ID!]!) {
        Font_getFonts(fontIds: $fontIds) {
          ...FontAssetFields
        }
      }
    `;
    return useQuery<GQLFontAsset, GQLQueryFont_GetFontsArgs>(query, { variables: args });
  }

  getFont(args: GQLQueryFont_GetFontArgs) {
    const query = gql`
      ${FONT_ASSET_FIELDS}
      query getFont($fontId: ID!) {
        Font_getFont(fontId: $fontId) {
          ...FontAssetFields
        }
      }
    `;
    return useQuery<GQLFontAsset, GQLQueryFont_GetFontArgs>(query, { variables: args });
  }
}
